/**
 * Drawing Warning Dialog
 * Shown when the user tries to switch to a chart type that hides drawings
 * while there are active drawings in the workspace. Drawings are hidden
 * (not deleted) while that chart type is active and restored when switching
 * back to a chart type that shows drawings.
 */
import * as AlertDialog from "@radix-ui/react-alert-dialog";

interface DrawingWarningDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  targetChartType: string;
  drawingCount: number;
}

function labelForChartType(type: string): string {
  // Favor a human label when available; fall back to raw type string.
  return type;
}

export function DrawingWarningDialog({
  open,
  onConfirm,
  onCancel,
  targetChartType,
  drawingCount,
}: DrawingWarningDialogProps) {
  return (
    <AlertDialog.Root open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <AlertDialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[340px] -translate-x-1/2 -translate-y-1/2 rounded-xl border p-5 shadow-2xl"
          style={{
            backgroundColor: "var(--bg-card)",
            borderColor: "var(--border-default)",
          }}
        >
          <AlertDialog.Title
            className="mb-1 text-sm font-semibold"
            style={{ color: "var(--text-primary)" }}
          >
            Switch to {labelForChartType(targetChartType)}?
          </AlertDialog.Title>
          <AlertDialog.Description
            className="mb-5 text-xs leading-relaxed"
            style={{ color: "var(--text-secondary)" }}
          >
            {labelForChartType(targetChartType)} will hide your drawings while
            this chart type is active. Your {drawingCount} drawing{drawingCount !== 1 ? "s" : ""}
            will be hidden (not deleted) and restored when you switch back to
            a chart type that shows drawings.
          </AlertDialog.Description>

          <div className="flex gap-2">
            <AlertDialog.Cancel asChild>
              <button
                type="button"
                onClick={onCancel}
                className="flex-1 rounded-lg border py-2 text-xs transition-colors hover:bg-surface-hover"
                style={{ borderColor: "var(--border-default)", color: "var(--text-secondary)" }}
              >
                Cancel
              </button>
            </AlertDialog.Cancel>
            <AlertDialog.Action asChild>
              <button
                type="button"
                onClick={onConfirm}
                className="flex-1 rounded-lg py-2 text-xs font-medium transition-all active:scale-95"
                style={{ backgroundColor: "var(--accent-primary)", color: "#fff" }}
              >
                Switch anyway
              </button>
            </AlertDialog.Action>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
